import { Injectable, Inject, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { CallNumber } from '@awesome-cordova-plugins/call-number/ngx';
import { take } from 'rxjs/operators';
import { Environment } from 'src/environments/environment';
import { Filters } from '../models/filters.model';
import { InitConfig } from '../models/initConfig.model';
import { GlobalConstService } from './global-const.service';
import { HttpService } from './http.service';
import { PlatformService } from './platform.service';
import { LoadingService } from './loading.service';
import { PageRouterService } from './page-router.service';
import { InAppBrowser, DefaultWebViewOptions, iOSViewStyle } from '@capacitor/inappbrowser';
import { Browser } from '@capacitor/browser';


@Injectable({ providedIn: 'root' })
export class LinksService {
  public initialConfigLink: InitConfig;
  private hasProcessedOAuthCode = false;
  public static readonly SYSTEM_LINK_CLASS = 'app-system-link';

  public linkType = {
    url: (link) => {
      console.log('🔗 linkType.url llamado con:', link);
      this.openInternalLink(link.target);
    },
    internal_url: (link) => {
      console.log('🔗 linkType.internal_url llamado con:', link);
      this.openInternalLink(link.target);
    }, // Maneja internal_url como url
    internal: (link) => {
      console.log('🔗 linkType.internal llamado con:', link);
      this.openInternalLink(link.target);
    }, // Maneja internal como url
    external_url: (link) => this.openExternalLink(link.target), // Validado por backend
    external: (link) => this.openExternalLink(link.target), // Validado por backend
    embedded_url: (link) => this.openEmbeddedLink(link.target, link.textToDisplay),
    embedded: (link) => this.openEmbeddedLink(link.target, link.textToDisplay), // Maneja embedded como embedded_url
    page: (link) => this.openPage(link),
    section: (link) => this.goToSection(link),
    global: () => this.goToNotifications()
  };

  private sectionType = {
    'section-notifications': (link) => this.router.navigate(['/notifications'], { state: { link } }),
    'section-contacts': (link) => this.router.navigate(['/directory'], { state: { link } }),
    'section-documents': (link) => this.router.navigate(['/documents'], { state: { link } }),
    'section-messages': () => this.router.navigate(['/chat/list']),
    'section-home': () => this.router.navigate(['/home']),
    'section-home-public': () => this.router.navigate(['/home-public']),
    'section-login': () => this.router.navigate(['/login/first']),
    'section-store': (link) => this.router.navigate(['/store'], { state: { link } }),
    'section-orders': (link) => this.router.navigate(['/store/orders'], { state: { link } }),
    'section-store-orders': (link) => this.router.navigate(['/store/orders'], { state: { link } }),
    '': () => this.router.navigate(['/home'])
  };


  constructor(
    private httpService: HttpService,
    private platformService: PlatformService,
    private router: Router,
    private callNumber: CallNumber,
    private loading: LoadingService,
    private pageRouter: PageRouterService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.document.addEventListener('click', this.onSystemLinkClick, true);
  }

  public getLinks(id, filters: Filters) {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/links/${id}/elements`, filters);
  }

  public initConversation(userId) {
    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/chat/private/${userId}`, {});
  }

  public async openInternalLink(unformattedLink: string) {
    console.log('🔗 ===== openInternalLink llamado =====');
    console.log('🔗 URL recibida:', unformattedLink);
    
    try {
      unformattedLink = this.createUrl(unformattedLink);
      console.log('🔗 URL después de createUrl:', unformattedLink);
      
      const isURL = this.isURL(unformattedLink);
      console.log('🔗 isURL:', isURL);

      if (!isURL) {
        console.log('🔗 No es URL válida, abriendo con el navegador del sistema');
        this.formatLink(unformattedLink, (url) => this.openSystemUrl(url));
        return;
      }

      // Verificar plataforma
      console.log('🔗 Verificando plataforma...');
      console.log('🔗 isIosWeb():', this.platformService.isIosWeb());
      console.log('🔗 isDesktop():', this.platformService.isDesktop());
      console.log('🔗 isWeb():', this.platformService.isWeb());
      console.log('🔗 isIos():', this.platformService.isIos());
      console.log('🔗 isAndroid():', this.platformService.isAndroid());

      // Si es iOS en versión web (Safari iOS), usar estrategia especial: abrir ventana placeholder inmediatamente
      // y actualizar su URL cuando llegue la respuesta del servidor.
      // Esta solución es específica para Safari iOS porque bloquea popups después de llamadas asíncronas.
      // En otros navegadores web (Chrome, Firefox, Edge, etc.) se usa el comportamiento estándar más abajo.
      if (this.platformService.isIosWeb()) {
        console.log('🔗 Safari iOS Web detectado, usando estrategia especial de ventana placeholder');
        console.log('🔗 URL original:', unformattedLink);
        
        // Abrir una ventana placeholder inmediatamente (dentro del contexto del evento de usuario)
        // Esto evita el bloqueo de Safari porque la ventana se abre sincrónicamente
        let newWindow: Window | null = null;
        try {
          console.log('🔗 Abriendo ventana placeholder (about:blank)');
          newWindow = window.open('about:blank', '_blank');
          
          if (!newWindow || newWindow.closed) {
            console.warn('🔗 ⚠️ No se pudo abrir ventana placeholder, Safari bloqueó el popup');
            // Si no se puede abrir, usar iframe como fallback
            this.formatLink(unformattedLink, (serverUrl) => {
              console.log('🔗 Usando iframe como fallback');
              this.openEmbeddedLink(serverUrl, '');
            });
            return;
          }
          
          console.log('🔗 ✅ Ventana placeholder abierta exitosamente');
          
          // Mostrar un mensaje de carga mientras esperamos la respuesta del servidor
          if (newWindow.document) {
            newWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Cargando...</title>
                  <style>
                    body {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      height: 100vh;
                      margin: 0;
                      background: #f5f5f5;
                    }
                    .loader {
                      text-align: center;
                    }
                    .spinner {
                      border: 4px solid #f3f3f3;
                      border-top: 4px solid #3498db;
                      border-radius: 50%;
                      width: 40px;
                      height: 40px;
                      animation: spin 1s linear infinite;
                      margin: 0 auto 20px;
                    }
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  </style>
                </head>
                <body>
                  <div class="loader">
                    <div class="spinner"></div>
                    <p>Cargando...</p>
                  </div>
                </body>
              </html>
            `);
            newWindow.document.close();
          }
        } catch (error) {
          console.error('🔗 ❌ Error abriendo ventana placeholder:', error);
          // Fallback: usar iframe
          this.formatLink(unformattedLink, (serverUrl) => {
            console.log('🔗 Usando iframe como fallback después de error');
            this.openEmbeddedLink(serverUrl, '');
          });
          return;
        }
        
        // Formatear el link y actualizar la ventana cuando llegue la respuesta del servidor
        this.formatLink(unformattedLink, (serverUrl) => {
          console.log('🔗 URL formateada del servidor recibida:', serverUrl);
          
          if (newWindow && !newWindow.closed) {
            try {
              console.log('🔗 Actualizando ventana con URL del servidor');
              newWindow.location.href = serverUrl;
              console.log('🔗 ✅ Ventana actualizada exitosamente');
            } catch (error) {
              console.error('🔗 ❌ Error actualizando ventana (posible CORS):', error);
              // Si falla por CORS, intentar usar window.open de nuevo
              try {
                console.log('🔗 Intentando abrir nueva ventana con URL del servidor');
                const fallbackWindow = window.open(serverUrl, '_blank');
                if (fallbackWindow) {
                  // Cerrar la ventana placeholder
                  newWindow.close();
                  console.log('🔗 ✅ Nueva ventana abierta, placeholder cerrada');
                } else {
                  console.warn('🔗 ⚠️ No se pudo abrir nueva ventana, usando iframe');
                  newWindow.close();
                  this.openEmbeddedLink(serverUrl, '');
                }
              } catch (e) {
                console.error('🔗 ❌ Error con fallback:', e);
                newWindow.close();
                this.openEmbeddedLink(serverUrl, '');
              }
            }
          } else {
            console.warn('🔗 ⚠️ La ventana placeholder fue cerrada, abriendo nueva ventana');
            try {
              const fallbackWindow = window.open(serverUrl, '_blank');
              if (!fallbackWindow || fallbackWindow.closed) {
                console.warn('🔗 ⚠️ No se pudo abrir nueva ventana, usando iframe');
                this.openEmbeddedLink(serverUrl, '');
              }
            } catch (error) {
              console.error('🔗 ❌ Error abriendo nueva ventana:', error);
              this.openEmbeddedLink(serverUrl, '');
            }
          }
        });
        
        return;
      }

      // Si es escritorio, abrir como embedded_url
      if (this.platformService.isDesktop()) {
        console.log('🔗 Es escritorio, usando embedded_url');
        this.formatLink(unformattedLink, (url) => {
          this.openEmbeddedLink(url, '');
        });
        return;
      }

      console.log('🔗 No es iOS Web ni Desktop, usando openBrowser');
      this.formatLink(unformattedLink, async (url) => {
        // Usar el navegador integrado
        await this.openBrowser(url);
      });
    } catch (error) {
      console.error('🔗 ❌ Error en openInternalLink:', error);
    }
    
    console.log('🔗 ===== Fin de openInternalLink =====');
  }

  isURL(unformattedLink) {
    return (
      !unformattedLink.includes('mailto:') &&
      !unformattedLink.includes('tel:') &&
      !unformattedLink.includes('fax:') &&
      !unformattedLink.includes('sms:') &&
      !unformattedLink.includes('callto:')
    );
  }

  /**
   * Normaliza una URL para comparación, removiendo parámetros de query y fragmentos
   * que puedan variar pero no cambien la página destino
   * @param url URL a normalizar
   * @returns URL normalizada para comparación
   */
  private normalizeUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      // Comparar solo protocolo, hostname y pathname
      // Ignorar search params y hash que pueden variar
      return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`;
    } catch (e) {
      // Si no es una URL válida, devolver tal cual
      return url;
    }
  }

  createUrl(unformattedLink) {
    if (typeof unformattedLink === 'string' && unformattedLink.startsWith('blob:')) {
      return unformattedLink;
    }
    const pattern = /^((http|https):\/\/)/;

    const isURL = this.isURL(unformattedLink);
    if (!pattern.test(unformattedLink) && isURL) {
      unformattedLink = `https://${unformattedLink}`;
    }

    return unformattedLink;
  }

  public async openExternalLink(unformattedLink: string) {
    console.log('🔗 openExternalLink llamado con:', unformattedLink);
    unformattedLink = this.createUrl(unformattedLink);
    console.log('🔗 URL después de createUrl:', unformattedLink);

    const isURL = this.isURL(unformattedLink);
    console.log('🔗 Es URL válida:', isURL);

    // Para external_url, SIEMPRE abrir en el navegador del sistema operativo
    if (this.platformService.isAndroid() || this.platformService.isIos()) {
      console.log('🔗 Plataforma nativa, abriendo en navegador del sistema');
      this.formatLink(unformattedLink, (url) => this.openSystemUrl(url));
      return;
    }

    // Para web, abrir en nueva pestaña
    // Safari (iOS y escritorio) bloquea popups después de llamadas asíncronas → usar estrategia de ventana placeholder
    if (this.platformService.isSafari()) {
      console.log('🔗 Safari detectado (iOS o escritorio), usando estrategia de ventana placeholder para external');
      this.openExternalLinkWithPlaceholderStrategy(unformattedLink);
      return;
    }

    console.log('🔗 Plataforma: Web');
    this.formatLink(unformattedLink, (url) => {
      console.log('🔗 URL formateada para Web:', url);
      console.log('🔗 Abriendo en nueva pestaña');
      window.open(url, '_blank');
    });
  }

  /**
   * Estrategia para abrir enlaces externos en Safari (iOS o escritorio).
   * Safari bloquea window.open() si se llama después de la respuesta del servidor (formatLink).
   * Se abre una ventana placeholder de forma síncrona y se actualiza con la URL cuando llega.
   */
  private openExternalLinkWithPlaceholderStrategy(unformattedLink: string): void {
    let newWindow: Window | null = null;
    try {
      newWindow = window.open('about:blank', '_blank');
      if (!newWindow || newWindow.closed) {
        console.warn('🔗 No se pudo abrir ventana placeholder, Safari bloqueó el popup');
        this.formatLink(unformattedLink, (serverUrl) => this.openEmbeddedLink(serverUrl, ''));
        return;
      }
      if (newWindow.document) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Cargando...</title>
              <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; background: #f5f5f5; }
                .loader { text-align: center; }
                .spinner { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 20px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              </style>
            </head>
            <body>
              <div class="loader"><div class="spinner"></div><p>Cargando...</p></div>
            </body>
          </html>
        `);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('🔗 Error abriendo ventana placeholder:', error);
      this.formatLink(unformattedLink, (serverUrl) => this.openEmbeddedLink(serverUrl, ''));
      return;
    }

    this.formatLink(unformattedLink, (serverUrl) => {
      if (newWindow && !newWindow.closed) {
        try {
          newWindow.location.href = serverUrl;
        } catch (error) {
          console.error('🔗 Error actualizando ventana (posible CORS):', error);
          try {
            const fallbackWindow = window.open(serverUrl, '_blank');
            if (fallbackWindow) {
              newWindow.close();
            } else {
              newWindow.close();
              this.openEmbeddedLink(serverUrl, '');
            }
          } catch (e) {
            newWindow.close();
            this.openEmbeddedLink(serverUrl, '');
          }
        }
      } else {
        try {
          const fallbackWindow = window.open(serverUrl, '_blank');
          if (!fallbackWindow || fallbackWindow.closed) {
            this.openEmbeddedLink(serverUrl, '');
          }
        } catch (error) {
          this.openEmbeddedLink(serverUrl, '');
        }
      }
    });
  }

  public openEmbeddedLink(unformattedLink: string, title: string) {
    this.formatLink(unformattedLink, (url) => this.router.navigate(['web-frame/view'], { queryParams: { url, title } }));
  }

  /**
   * Método directo para abrir enlaces externos sin depender del servidor
   */
  public async openExternalLinkDirect(url: string) {
    await this.openSystemUrl(this.createUrl(url));
  }

  /**
   * Abre una URL http(s) con el navegador del sistema (Safari View / Custom Tabs).
   * En web usa una pestaña nueva. Evita Cordova InAppBrowser en iOS.
   */
  public async openSystemUrl(rawUrl: string): Promise<void> {
    if (!rawUrl) {
      return;
    }

    const url = this.createUrl(rawUrl);

    if (url.includes('tel:')) {
      this.callNumber.callNumber(url.replace('tel:', ''), true);
      return;
    }

    const isHttpUrl = /^https?:\/\//i.test(url);
    if (!isHttpUrl || this.platformService.isWeb()) {
      window.open(url, '_blank');
      return;
    }

    try {
      await this.loading.hide();
      await Browser.open({ url });
    } catch (error) {
      console.error('🔗 Error abriendo navegador del sistema:', error);
      window.open(url, '_blank');
    }
  }

  private onSystemLinkClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest?.(`a.${LinksService.SYSTEM_LINK_CLASS}, a[target="_system"], a._system`) as HTMLAnchorElement | null;
    if (!anchor) {
      return;
    }

    const url = this.getSystemLinkUrl(anchor);
    if (!url) {
      return;
    }

    if (this.platformService.isWeb() && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) {
      return;
    }

    event.preventDefault();
    this.openSystemUrl(url);
  };

  private getSystemLinkUrl(anchor: HTMLAnchorElement): string | null {
    const href = anchor.getAttribute('href');
    if (!href || href === '#' || href.toLowerCase().startsWith('javascript:')) {
      return null;
    }
    return href;
  }

  private formatLink(unformattedLink: string, callback: (value: any) => void) {
    console.log('🔗 formatLink llamado con:', unformattedLink);
    this.formatLinkRequest(unformattedLink).subscribe({
      next: (res) => {
        console.log('🔗 Respuesta del servidor:', res);
        console.log('🔗 Ejecutando callback con respuesta del servidor');
        callback(res);
      },
      error: (error) => {
        console.error('🔗 Error formateando link, usando URL original:', error);
        console.log('🔗 Ejecutando callback con URL original como fallback');
        // Fallback: usar la URL original si el servidor falla
        callback(unformattedLink);
      }
    });
  }

  formatLinkRequest(unformattedLink) {
    return this.httpService
      .myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/links/link/format`, { url: unformattedLink })
      .pipe(take(1));
  }

  private openPage(link) {
    // Para el caso de que un enlace pueda ir directamente al detalle de un elemento
    const ELEMENT_COMPONENT_TYPE_ROUTE_MAP = {
      blog: 'blog',
      event: 'calendar',
      contact: 'contacts',
      agenda: 'contacts',
      podcast: 'podcast'
    };
    // Para el caso de que un enlace pueda ir directamente al listado de un componente específico
    const COMPONENT_TYPE_ROUTE_MAP = {
      blog: 'blog',
      event: 'calendar',
      document: 'documents',
      contact: 'contacts',
      gallery: 'gallery',
      link: 'links',
      podcast: 'podcast',
      personalDocument: 'personal-documents',
      embedded: 'embeddings'
    };

    if (link.pageId && link.componentId && Object.keys(ELEMENT_COMPONENT_TYPE_ROUTE_MAP).includes(link.componentType) && link.elementId) {
      if (link.componentType === 'agenda') {
        this.router.navigate([
          `/${ELEMENT_COMPONENT_TYPE_ROUTE_MAP[link.componentType]}/${link.componentId}/detail/${link.elementId}/${link.componentType}`
        ]);
        return;
      }

      if (link.componentType === 'podcast') {
        this.router.navigate([`/${ELEMENT_COMPONENT_TYPE_ROUTE_MAP[link.componentType]}/episode`], {
          queryParams: { componentId: link.componentId, episodeId: link.elementId }
        });
        return;
      }

      this.router.navigate([`/${ELEMENT_COMPONENT_TYPE_ROUTE_MAP[link.componentType]}/${link.componentId}/detail/${link.elementId}`]);
      return;
    }

    if (link.pageId && link.componentId && Object.keys(COMPONENT_TYPE_ROUTE_MAP).includes(link.componentType)) {
      this.router.navigate([`/${COMPONENT_TYPE_ROUTE_MAP[link.componentType]}/${link.componentId}/list`]);
      return;
    }

    if (link.pageId) {
      // Usar PageRouterService para evaluar el tipo de página
      this.pageRouter.navigateToPage(link.pageId);
      return;
    }

    if (link.target) {
      // Usar PageRouterService para evaluar el tipo de página
      this.pageRouter.navigateToPage(link.target);
    }
  }

  private goToSection(link) {
    console.log('goToSection: ', link);

    const sectionTarget = link.target;
    
    const handlerSectionType = this.sectionType[sectionTarget];
    if (handlerSectionType) {
      handlerSectionType(link);
    } else {
      console.warn(`Section type "${sectionTarget}" not found, navigating to home`);
      this.router.navigate(['/home']);
    }
    this.loading.hide();
  }

  private goToNotifications() {
    this.router.navigate(['/notifications']);
  }

  /**
   * Abre una URL directamente en el navegador integrado sin formateo
   * Útil para URLs completas como OAuth que no necesitan procesamiento
   * @param url URL completa a abrir
   * @param redirectUrl URL de redirección para detectar el cierre automático (opcional)
   */
  public async openDirectUrl(url: string, redirectUrl?: string): Promise<any> {
    if (redirectUrl) {
      return await this.openBrowserWithRedirect(url, redirectUrl);
    }
    return await this.openBrowser(url);
  }

  /**
   * Abre una URL en el navegador integrado con manejo de redirección automática
   * @param url URL a abrir
   * @param redirectUrl URL de redirección para detectar el cierre automático
   */
  private async openBrowserWithRedirect(url: string, redirectUrl: string): Promise<any> {
    try {
      if (this.platformService.isWeb()) {
        console.log('Abriendo URL en web:', url);
        window.open(url, '_blank');
        return Promise.resolve();
      }

      console.log('🔗 Abriendo OAuth con Capacitor InAppBrowser');

      // iOS no puede presentar el browser si el loading (u otro modal) sigue visible
      await this.loading.hide();
      await new Promise((resolve) => setTimeout(resolve, 350));

      this.hasProcessedOAuthCode = false;
      InAppBrowser.removeAllListeners();

      await InAppBrowser.addListener('browserPageNavigationCompleted', (data) => {
        console.log('🔗 Capacitor InAppBrowser navigation:', data.url);
        this.handleOAuthNavigation(data?.url, redirectUrl);
      });

      await InAppBrowser.addListener('browserClosed', () => {
        console.log('🔗 Capacitor InAppBrowser cerrado');
        InAppBrowser.removeAllListeners();
      });

      await InAppBrowser.openInWebView({
        url,
        options: {
          ...DefaultWebViewOptions,
          showNavigationButtons: false,
          showURL: false,
          showToolbar: true,
          clearCache: true,
          clearSessionCache: true,
          closeButtonText: 'Cerrar',
          iOS: {
            ...DefaultWebViewOptions.iOS,
            viewStyle: iOSViewStyle.FULL_SCREEN,
            allowOverScroll: true,
            enableViewportScale: false,
            allowInLineMediaPlayback: false,
            surpressIncrementalRendering: false,
            allowsBackForwardNavigationGestures: true,
            animationEffect: 1
          },
          android: {
            ...DefaultWebViewOptions.android,
            allowZoom: false,
            hardwareBack: true,
            pauseMedia: true
          }
        }
      });
    } catch (error) {
      console.error('🔗 Error abriendo navegador con redirección:', error);
      throw error;
    }
  }

  private handleOAuthNavigation(url: string, redirectUrl?: string) {
    if (!url || this.hasProcessedOAuthCode) {
      return;
    }

    console.log('🔗 Procesando URL de navegación:', url);

    const urlSuccessPage = 'redirect/finish';

    if (url.includes(urlSuccessPage)) {
      console.log('🔗 ✅ Detectada URL de éxito directa, procesando OAuth...');

      const token = url.split('sid=')[1];
      console.log('🔗 Token extraído:', token);

      if (token) {
        this.hasProcessedOAuthCode = true;
        this.closeOAuthBrowser().finally(() => {
          this.loading.show('Iniciando sesión...');
          this.processOAuthFromToken(token);
        });
      } else {
        console.error('🔗 ❌ No se pudo extraer el token de la URL');
      }
      return;
    }

    if (redirectUrl && url.includes(redirectUrl)) {
      console.log('🔗 Redirect de Azure detectado, esperando página de éxito...');
      return;
    }

    console.log('🔗 URL no reconocida, continuando...');
  }

  private async closeOAuthBrowser(): Promise<void> {
    try {
      console.log('🔗 Cerrando InAppBrowser...');
      await InAppBrowser.close();
    } catch (error) {
      console.warn('🔗 No se pudo cerrar InAppBrowser:', error);
    }
  }


  /**
   * Procesa el OAuth con el token extraído (igual que el código anterior de Cordova)
   * @param token Token JWT extraído de la URL
   */
  private processOAuthFromToken(token: string) {
    try {
      console.log('Procesando OAuth con token:', token);
      
      if (token) {
        console.log('Token JWT encontrado:', token);
        
        // Emitir evento para que la app procese el login (igual que Cordova)
        const event = new CustomEvent('oauth-completed', {
          detail: {
            sessionToken: token
          }
        });
        window.dispatchEvent(event);
        
      } else {
        console.error('No se encontró el token en la URL');
      }
    } catch (error) {
      console.error('Error procesando OAuth con token:', error);
    }
  }

  /**
   * Crea un elemento <a> preparado para Safari iOS dentro del contexto del evento de usuario
   * El elemento se crea inmediatamente pero el click se hará después cuando llegue la URL del servidor
   * @returns El elemento <a> creado
   */
  private createLinkElementForSafari(): HTMLAnchorElement {
    // Crear un elemento <a> temporal con target="_blank"
    const link = document.createElement('a');
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Hacer el elemento invisible pero presente en el DOM
    link.style.display = 'none';
    link.style.position = 'absolute';
    link.style.left = '-9999px';
    document.body.appendChild(link);
    
    console.log('🔗 Elemento <a> creado para Safari, esperando URL del servidor...');
    return link;
  }

  /**
   * Abre un link en una nueva pestaña usando un elemento <a> temporal
   * Esto evita el bloqueo de popups en Safari iOS porque el click se ejecuta
   * sincrónicamente dentro del contexto del evento de usuario
   * @param url URL a abrir
   */
  private openLinkInNewTabSafari(url: string): void {
    try {
      // Crear un elemento <a> temporal con target="_blank"
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Hacer el elemento invisible pero presente en el DOM
      link.style.display = 'none';
      link.style.position = 'absolute';
      link.style.left = '-9999px';
      document.body.appendChild(link);
      
      // Hacer click programático - Safari lo trata como acción del usuario
      // IMPORTANTE: Esto debe ejecutarse sincrónicamente dentro del contexto del evento
      link.click();
      
      // Limpiar el elemento después de un breve delay
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, 100);
      
      console.log('🔗 Link abierto en nueva pestaña usando método Safari (elemento <a>):', url);
    } catch (error) {
      console.error('🔗 Error abriendo link con método Safari, usando fallback:', error);
      // Fallback: intentar window.open directamente
      try {
        const newWindow = window.open(url, '_blank');
        if (!newWindow || newWindow.closed) {
          console.warn('🔗 window.open fue bloqueado, usando iframe como último recurso');
          this.openEmbeddedLink(url, '');
        }
      } catch (e) {
        console.warn('🔗 Error con window.open, usando iframe como último recurso:', e);
        this.openEmbeddedLink(url, '');
      }
    }
  }

  /**
   * Abre una URL en el navegador integrado
   * @param url URL a abrir
   */
  private async openBrowser(url: string): Promise<any> {
    try {
      // Si estamos en web, usar window.open() directamente
      if (this.platformService.isWeb()) {
        console.log('Abriendo URL en web:', url);
        window.open(url, '_blank');
        return Promise.resolve();
      }

      await this.loading.hide();

      const result = await InAppBrowser.openInWebView({
        url: url,
        options: {
          ...DefaultWebViewOptions,
          showNavigationButtons: false,
          iOS: {
            viewStyle: 2,
            allowOverScroll: true,
            enableViewportScale: true,
            allowInLineMediaPlayback: false,
            surpressIncrementalRendering: false,
            allowsBackForwardNavigationGestures: true,
            animationEffect: 1
          },
          android: {
            allowZoom: false,
            hardwareBack: true,
            pauseMedia: true
          },
          showURL: false,
          closeButtonText: '✕' 
        }
      });
      return result;
    } catch (error) {
      console.error('Error abriendo navegador:', error);
      throw error;
    }
  }
}
